import {
  DataLayerTrackEvents,
  DatalayerPopupContentTypes,
} from '@/datalayer/App.datalayer.constants';
import { mapTargetBySelectors } from '@/features/shared/HOCs/withGTM';
import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import { isExternalLinkContent, isURLWithFile, matchExternalLink } from '@/utils/helpers';

enum Selectors {
  // Target
  Close = '.gtm-news-ribbon-close',
  Click = '.gtm-news-ribbon-click  a',
  // Containers
  ClickContainer = '.gtm-news-ribbon-click',
}

export const mapTarget = mapTargetBySelectors([Selectors.Close, Selectors.Click]);

export const onDatalayer = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const isClose = target?.classList.contains(Selectors.Close.replace('.', ''));
  const isClick = target?.closest(Selectors.ClickContainer);
  const variantName = (target?.closest('[data-datalayer-variant]') as HTMLDivElement)?.dataset
    ?.datalayerVariant;
  const title = (target?.closest('[data-datalayer-title]') as HTMLDivElement)?.dataset
    ?.datalayerTitle;
  if (isClose) {
    return {
      event: 'closeNews',
      eventCategory: 'Primary Nav',
      ...(variantName && { variant_name: variantName }),
      ...(title && { title }),
    };
  }

  if (isClick) {
    return {
      event: 'clickNewsLink',
      eventCategory: 'Primary Nav',
      ...(variantName && { variant_name: variantName }),
    };
  }

  return undefined;
};

const getUrlType = (href = '') => {
  if (isURLWithFile(href)) return 'file_download';

  if (isExternalLinkContent(href)) return 'external_link';

  return 'internal_link';
};

export const onDatalayerNew = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const isClose = target?.classList.contains(Selectors.Close.replace('.', ''));
  const isClick = target?.closest(Selectors.ClickContainer);
  const container = target?.closest('[data-datalayer-position]') as HTMLDivElement;
  const position = container?.dataset?.datalayerPosition || '';
  const variantName = (target?.closest('[data-datalayer-variant]') as HTMLDivElement)?.dataset
    ?.datalayerVariant;
  const title = (target?.closest('[data-datalayer-title]') as HTMLDivElement)?.dataset
    ?.datalayerTitle;

  if (isClose) {
    return {
      event: 'trackEvent-newsRibbonClose',
      event_name: 'internal_newsribbon_close',
      eventCategory: 'engagement',
      eventAction: 'internal_newsribbon_close',
      position,
      ...(variantName && { variant_name: variantName }),
      ...(title && { title }),
    };
  }

  if (isClick) {
    const href = target?.href || '';
    const { textContent } = target;

    return {
      event: 'trackEvent-newsRibbonClick',
      event_name: 'internal_newsribbon_click',
      eventCategory: 'engagement',
      eventAction: 'internal_newsribbon_click',
      eventLabel: textContent?.trim(),
      position,
      link_url: href,
      link_text: textContent?.trim(),
      type: getUrlType(href),
      ...(variantName && { variant_name: variantName }),
      ...(title && { title }),
    };
  }

  return undefined;
};

export const onDatalayerClose = ({ target }: OnDatalayerProps) => {
  const { position, variantName = '', title } = target;

  if (!target) {
    return undefined;
  }

  return {
    event: 'trackEvent-newsRibbonClose',
    event_name: 'internal_newsribbon_close',
    eventCategory: 'engagement',
    eventAction: 'internal_newsribbon_close',
    position,
    ...(variantName && { variant_name: variantName }),
    ...(title && { title }),
  };
};

export const onDatalayerExitPopup = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }
  const { title } = target;

  return {
    event: DataLayerTrackEvents.EXIT_POPUP,
    event_name: 'exit_popup',
    content_type: DatalayerPopupContentTypes.NEWS_RIBBON,
    ...(!!title && { title }),
  };
};
